"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  firebaseIsConfigured,
  observeSignedInUser,
  readPerzaAdminAccess,
  signInWithApple,
  signInWithGoogle,
  signOutOfPortal,
  type PerzaAdminAccess,
} from "./firebase-client";
import { loadPortalMetrics, type MethodMetric, type PortalViewData } from "./metrics-client";
import perzaLogo from "../../assets/images/perza-logo.png";
import perzaMark from "../../assets/images/perza-mark.png";

type NavKey = "overview" | "profitability" | "methods" | "providers" | "reconciliation" | "settings";

const navItems: Array<{ key: NavKey; label: string; icon: string }> = [
  { key: "overview", label: "Overview", icon: "⌂" },
  { key: "profitability", label: "Profitability", icon: "↗" },
  { key: "methods", label: "Earn methods", icon: "◇" },
  { key: "providers", label: "Providers", icon: "◎" },
  { key: "reconciliation", label: "Reconciliation", icon: "✓" },
  { key: "settings", label: "Settings", icon: "⚙" },
];

const PLAYGAMA_LIMITATION = "Playgama revenue is not directly attributable — it enters as a shared pool alongside Playgama's own ad monetization and cannot be split per-session. Reward cost is tracked accurately; revenue is understated.";
const QUIZ_RETENTION_NOTE = "Deliberate daily retention cost with no direct revenue; this is not a performance problem.";

const previewMethods = [
  { name: "Surveys", provider: "TapResearch + ayeT", revenue: 620.45, cost: 268.2, margin: 352.25, users: "1,842", completion: "18.4%", tone: "violet" },
  { name: "Offers", provider: "ayeT", revenue: 598.34, cost: 141.7, margin: 456.64, users: "924", completion: "9.7%", tone: "blue" },
  { name: "Games", provider: "Playgama", revenue: 402.11, cost: 198.4, margin: 203.71, users: "2,106", completion: "42.1%", tone: "amber" },
  { name: "Rewarded ads", provider: "AdMob", revenue: 221.84, cost: 34.1, margin: 187.74, users: "1,387", completion: "71.6%", tone: "green" },
  { name: "Quizzes", provider: "Perza", revenue: 0, cost: 50.4, margin: -50.4, users: "1,063", completion: "64.8%", tone: "rose" },
];

const previewProviders = [
  { name: "AdMob", note: "Reporting current", status: "Connected", value: "$221.84", detail: "48,920 impressions", tone: "green" },
  { name: "Google Analytics", note: "Events current", status: "Connected", value: "4,826", detail: "active users", tone: "blue" },
  { name: "TapResearch", note: "Callbacks current", status: "Connected", value: "$438.19", detail: "publisher revenue", tone: "violet" },
  { name: "ayeT", note: "Callbacks current", status: "Connected", value: "$780.60", detail: "publisher revenue", tone: "amber" },
  { name: "Playgama", note: "Awaiting reporting access", status: "Needs API", value: "$402.11", detail: "latest import", tone: "rose" },
];

const previewDaily = [
  { day: "Mon", revenue: 66, cost: 29 },
  { day: "Tue", revenue: 74, cost: 35 },
  { day: "Wed", revenue: 69, cost: 33 },
  { day: "Thu", revenue: 83, cost: 38 },
  { day: "Fri", revenue: 77, cost: 34 },
  { day: "Sat", revenue: 92, cost: 41 },
  { day: "Sun", revenue: 88, cost: 39 },
];

const previewMetrics: PortalViewData = {
  generatedAt: Date.now(),
  economicsConfigured: true,
  economicsSource: "configuration",
  truncated: false,
  summary: { grossRevenue: 1842.74, rewardLiability: 692.8, contributionMargin: 1149.94, activeEarners: 3608, rewardCredits: 692800 },
  revenueBreakdown: { earnMethodRevenue: 1218.79, displayAdRevenue: 221.84, otherAttributedRevenue: 402.11 },
  methods: previewMethods,
  providers: previewProviders,
  daily: previewDaily,
  quality: { earningEvents: 1200, revenueAttributedEvents: 930, missingRevenueEvents: 270, revenueCoverage: .775, costBasis: "configuration" },
  retention: {
    relaunchAt: "2026-08-13T00:00:00.000Z", timezone: "UTC", cohortUsers: 84,
    definition: "First qualifying post-relaunch earning action.",
    metrics: [{ day: 1, eligible: 62, retained: 26, rate: .419 }, { day: 7, eligible: 31, retained: 9, rate: .29 }, { day: 30, eligible: 0, retained: 0, rate: null }],
    cohorts: [],
  },
  cashouts: {
    requested: { count: 9, usd: 38 }, pendingProcessing: { count: 2, usd: 8 }, fulfilled: { count: 6, usd: 27 }, failedRefunded: { count: 1, usd: 3 }, fulfilledAtFallbacks: 0,
    costBasis: "Gift-card face value; exact Reloadly charges require manual reconciliation.",
  },
};
const emptyMetrics: PortalViewData = {
  generatedAt: 0,
  economicsConfigured: true,
  economicsSource: "redemption-face-value",
  truncated: false,
  summary: { grossRevenue: 0, rewardLiability: 0, contributionMargin: 0, activeEarners: 0, rewardCredits: 0 },
  revenueBreakdown: { earnMethodRevenue: 0, displayAdRevenue: 0, otherAttributedRevenue: 0 },
  methods: previewMethods.map((method) => ({ ...method, revenue: 0, cost: 0, margin: 0, users: "0", completion: "0 rewards" })),
  providers: previewProviders.map((provider) => ({ ...provider, status: "Unavailable", value: "—", detail: "Live data did not load", note: "No preview data shown" })),
  daily: [],
  quality: { earningEvents: 0, revenueAttributedEvents: 0, missingRevenueEvents: 0, revenueCoverage: 0, costBasis: "redemption-face-value" },
  retention: { relaunchAt: "2026-08-13T00:00:00.000Z", timezone: "UTC", definition: "First qualifying post-relaunch earning action.", cohortUsers: 0, metrics: [{ day: 1, eligible: 0, retained: 0, rate: null }, { day: 7, eligible: 0, retained: 0, rate: null }, { day: 30, eligible: 0, retained: 0, rate: null }], cohorts: [] },
  cashouts: { requested: { count: 0, usd: 0 }, pendingProcessing: { count: 0, usd: 0 }, fulfilled: { count: 0, usd: 0 }, failedRefunded: { count: 0, usd: 0 }, fulfilledAtFallbacks: 0, costBasis: "Gift-card face value; exact Reloadly charges require manual reconciliation." },
};
const MetricsContext = createContext<PortalViewData>(previewMetrics);
const useMetrics = () => useContext(MetricsContext);

function PerzaBrand({ compact = false, admin = false }: { compact?: boolean; admin?: boolean }) {
  return (
    <div className={`perza-brand ${compact ? "compact" : ""}`}>
      <img src={compact ? perzaMark : perzaLogo} alt="Perza" />
      {admin && <span>Admin</span>}
    </div>
  );
}

function money(value: number) {
  return value.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function MethodStatus({ method, showNote = false }: { method: MethodMetric; showNote?: boolean }) {
  if (method.name === "Quizzes") {
    return <span className="method-status-wrap" title={QUIZ_RETENTION_NOTE}><span className="status neutral">Retention tool</span>{showNote && <small className="status-note">Daily retention cost · no direct revenue</small>}</span>;
  }
  const pending = method.margin === null;
  const warning = (method.margin ?? 0) < 0;
  return <span className={pending || warning ? "status warning" : "status healthy"}>{pending ? "Pending" : warning ? "Review" : method.revenueEstimated ? "Estimated healthy" : "Healthy"}</span>;
}

function RetentionValue({ day, rate }: { day: number; rate: number | null }) {
  if (rate !== null) return <strong>{(rate * 100).toFixed(1)}%</strong>;
  const wait = day === 1 ? "24 hours" : `${day} days`;
  const explanation = `D${day} available after ${wait} from cohort start date.`;
  return <button type="button" className="pending-value" aria-label={`Pending. ${explanation}`} data-tooltip={explanation}>Pending</button>;
}

export function AdminPortal() {
  const previewMode = import.meta.env.DEV;
  const [authenticated, setAuthenticated] = useState(previewMode);
  const [adminAccess, setAdminAccess] = useState<PerzaAdminAccess | null>(previewMode ? {
    uid: "preview",
    displayName: "Mach2",
    email: "",
    role: "super_admin",
  } : null);
  const [checkingSession, setCheckingSession] = useState(!previewMode && firebaseIsConfigured);
  const [busy, setBusy] = useState<"google" | "apple" | null>(null);
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    if (previewMode) return;
    return observeSignedInUser(async (user) => {
      if (!user) {
        setAuthenticated(false);
        setAdminAccess(null);
        setCheckingSession(false);
        return;
      }
      const access = await readPerzaAdminAccess(user).catch(() => null);
      setAdminAccess(access);
      setAuthenticated(access !== null);
      if (!access) await signOutOfPortal();
      setCheckingSession(false);
    });
  }, [previewMode]);

  async function login(provider: "google" | "apple") {
    if (!firebaseIsConfigured) {
      setAuthError("Firebase web credentials have not been added yet.");
      return;
    }
    setBusy(provider);
    setAuthError("");
    try {
      const result = provider === "google" ? await signInWithGoogle() : await signInWithApple();
      const access = await readPerzaAdminAccess(result.user);
      if (!access) {
        await signOutOfPortal();
        setAuthError("This Perza account does not have an active administrator role.");
        return;
      }
      setAdminAccess(access);
      setAuthenticated(true);
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : "Sign-in could not be completed.");
    } finally {
      setBusy(null);
    }
  }

  if (checkingSession) return <main className="session-loader"><PerzaBrand compact /><p>Verifying administrator access…</p></main>;
  if (!authenticated) return <LoginScreen busy={busy} error={authError} onLogin={login} />;
  return <Dashboard access={adminAccess!} onSignOut={async () => { await signOutOfPortal(); setAdminAccess(null); setAuthenticated(false); }} />;
}

function LoginScreen({ busy, error, onLogin }: { busy: "google" | "apple" | null; error: string; onLogin: (provider: "google" | "apple") => void }) {
  return (
    <main className="login-shell">
      <section className="login-brand">
        <PerzaBrand />
        <div className="login-copy">
          <p className="eyebrow light">Private operations</p>
          <h1>Know what is earning.<br />Know what is costing.</h1>
          <p>One secure view across Perza revenue, rewards, engagement, and provider health.</p>
        </div>
        <div className="login-stat-row">
          <div><strong>5</strong><span>data sources</span></div>
          <div><strong>24/7</strong><span>margin watch</span></div>
          <div><strong>1</strong><span>trusted view</span></div>
        </div>
      </section>
      <section className="login-panel">
        <div className="login-card">
          <span className="secure-badge">Admin access only</span>
          <h2>Sign in to Perza Admin</h2>
          <p>Use your existing Perza account. Access follows the administrator role already assigned in Perza.</p>
          <button className="auth-button" onClick={() => onLogin("google")} disabled={busy !== null}>
            <span className="google-g">G</span>{busy === "google" ? "Connecting…" : "Continue with Google"}
          </button>
          <button className="auth-button dark" onClick={() => onLogin("apple")} disabled={busy !== null}>
            <span className="apple-mark">●</span>{busy === "apple" ? "Connecting…" : "Continue with Apple"}
          </button>
          {error && <p className="auth-error" role="alert">{error}</p>}
          <p className="login-footnote">Protected by Firebase Authentication and server-side authorization.</p>
        </div>
      </section>
    </main>
  );
}

function Dashboard({ access, onSignOut }: { access: PerzaAdminAccess; onSignOut: () => void }) {
  const [active, setActive] = useState<NavKey>("overview");
  const [range, setRange] = useState("Last 7 days");
  const [mobileNav, setMobileNav] = useState(false);
  const [metrics, setMetrics] = useState(access.uid === "preview" ? previewMetrics : emptyMetrics);
  const [metricsState, setMetricsState] = useState<"preview" | "loading" | "live" | "error">(access.uid === "preview" ? "preview" : "loading");
  const title = navItems.find((item) => item.key === active)?.label ?? "Overview";
  const days = range === "Today" ? 1 : range === "Last 30 days" ? 30 : range === "This quarter" ? 90 : 7;

  useEffect(() => {
    if (access.uid === "preview") return;
    let activeRequest = true;
    loadPortalMetrics(days).then((next) => {
      if (!activeRequest) return;
      setMetrics(next);
      setMetricsState("live");
    }).catch(() => { if (activeRequest) setMetricsState("error"); });
    return () => { activeRequest = false; };
  }, [access.uid, days]);

  return (
    <div className="dashboard-shell">
      <aside className={`sidebar ${mobileNav ? "open" : ""}`}>
        <div className="sidebar-top">
          <PerzaBrand compact admin />
          <button className="mobile-close" onClick={() => setMobileNav(false)} aria-label="Close navigation">×</button>
        </div>
        <nav aria-label="Admin navigation">
          <p className="nav-label">Workspace</p>
          {navItems.map((item) => (
            <button key={item.key} className={active === item.key ? "active" : ""} onClick={() => { setActive(item.key); setMobileNav(false); }}>
              <span>{item.icon}</span>{item.label}
              {item.key === "providers" && <i>1</i>}
            </button>
          ))}
        </nav>
        <div className="sidebar-status">
          <span className={`live-dot ${metricsState === "error" ? "error" : ""}`} />
          <div><strong>{metricsState === "live" ? "Live Perza data" : metricsState === "loading" ? "Loading data" : metricsState === "error" ? "Data unavailable" : "Preview data"}</strong><small>{metricsState === "live" ? "Read-only connection" : metricsState === "preview" ? "Production is disconnected" : "No balances can be changed"}</small></div>
        </div>
        <button className="profile-button" onClick={onSignOut}>
          <span className="avatar">{access.displayName.slice(0, 2).toUpperCase()}</span>
          <span><strong>{access.displayName}</strong><small>{access.role === "super_admin" ? "Super administrator" : "Administrator"}</small></span>
          <span className="signout">↗</span>
        </button>
      </aside>
      {mobileNav && <button className="nav-scrim" onClick={() => setMobileNav(false)} aria-label="Close navigation" />}

      <main className="dashboard-main">
        <header className="topbar">
          <button className="menu-button" onClick={() => setMobileNav(true)} aria-label="Open navigation">☰</button>
          <div><p className="breadcrumb">Perza / {title}</p><h1>{title}</h1></div>
          <div className="topbar-actions">
            <span className="updated"><i />Updated {metricsState === "live" ? new Date(metrics.generatedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "preview"}</span>
            <select value={range} onChange={(event) => setRange(event.target.value)} aria-label="Reporting period">
              <option>Today</option><option>Last 7 days</option><option>Last 30 days</option><option>This quarter</option>
            </select>
          </div>
        </header>
        <MetricsContext.Provider value={metrics}><div className="content-wrap">
          {metricsState === "error" && <div className="data-banner error">Live metrics could not be loaded. Your account remains signed in and no data was changed.</div>}
          {metricsState === "live" && metrics.economicsSource === "redemption-face-value" && <div className="data-banner">Reward cost uses Perza’s redemption face value of $1 per 10,000 PZP. Add an explicit reward-economics override only if fulfillment cost differs.</div>}
          {metricsState === "live" && metrics.quality.missingRevenueEvents > 0 && <div className="data-banner">Profitability includes recorded provider revenue only. {metrics.quality.missingRevenueEvents.toLocaleString("en-US")} earning events in this period have no revenue attribution, primarily AdMob, Playgama, or Perza-owned methods.</div>}
          {active === "overview" && <Overview range={range} setActive={setActive} />}
          {active === "profitability" && <Profitability />}
          {active === "methods" && <EarnMethods />}
          {active === "providers" && <Providers />}
          {active === "reconciliation" && <Reconciliation />}
          {active === "settings" && <Settings />}
        </div></MetricsContext.Provider>
      </main>
    </div>
  );
}

function Overview({ range, setActive }: { range: string; setActive: (key: NavKey) => void }) {
  const metrics = useMetrics();
  const { daily, providers } = metrics;
  const maxBar = useMemo(() => Math.max(1, ...daily.map((item) => item.revenue)), [daily]);
  const marginPercent = metrics.summary.contributionMargin === null || metrics.summary.grossRevenue <= 0 ? null : metrics.summary.contributionMargin / metrics.summary.grossRevenue;
  const quiz = metrics.methods.find((method) => method.name === "Quizzes");
  return (
    <>
      <section className="intro-row">
        <div><p className="eyebrow">Operating snapshot</p><h2>{marginPercent === null ? "Profitability is being measured." : marginPercent >= .2 ? "Recorded earnings exceed reward cost." : "Recorded margin needs attention."}</h2><p>Read-only provider and reward activity for {range.toLowerCase()}, with missing revenue attribution called out explicitly.</p></div>
        <div className="health-pill"><span>{marginPercent === null ? "—" : Math.max(0, Math.round(marginPercent * 100))}</span><div><strong>{marginPercent === null ? "Pending" : marginPercent >= .2 ? "Healthy" : "Review"}</strong><small>Margin score</small></div></div>
      </section>
      <section className="metric-grid">
        <MetricCard label="Recorded revenue" value={money(metrics.summary.grossRevenue)} delta="Live" note="provider callbacks" tone="ink" />
        <MetricCard label="Reward liability" value={metrics.summary.rewardLiability === null ? "Not set" : money(metrics.summary.rewardLiability)} delta="Live" note={metrics.economicsSource === "configuration" ? "configured PZP cost" : "redemption face value"} tone="amber" />
        <MetricCard label="Contribution margin" value={metrics.summary.contributionMargin === null ? "Not set" : money(metrics.summary.contributionMargin)} delta={marginPercent === null ? "Pending" : `${(marginPercent * 100).toFixed(1)}%`} note="recorded sources only" tone="green" />
        <MetricCard label="Active earners" value={metrics.summary.activeEarners.toLocaleString("en-US")} delta="Live" note={`${metrics.summary.rewardCredits.toLocaleString("en-US")} PZP issued`} tone="violet" />
      </section>
      <section className="overview-grid">
        <article className="panel chart-panel">
          <div className="panel-heading"><div><p className="eyebrow">Performance</p><h3>Revenue and reward cost</h3></div><div className="legend"><span className="revenue-key">Revenue</span><span className="cost-key">Cost</span></div></div>
          <div className="chart-summary"><strong>{money(metrics.summary.grossRevenue)}</strong><span>Recorded revenue</span></div>
          <div className="bar-chart" aria-label="Daily revenue and reward cost chart">
            {daily.map((item, index) => <div className="bar-day" key={`${item.day}-${index}`}><div className="bars"><i className="revenue-bar" style={{ height: `${(item.revenue / maxBar) * 100}%` }} /><i className="cost-bar" style={{ height: `${((item.cost ?? 0) / maxBar) * 100}%` }} /></div><span>{item.day}</span></div>)}
          </div>
        </article>
        <article className="panel attention-panel">
          <div className="panel-heading"><div><p className="eyebrow">Operating context</p><h3>Retention investment</h3></div><span className="alert-count neutral">i</span></div>
          <div className="alert-card neutral"><span className="alert-icon neutral">i</span><div><strong>{metrics.economicsConfigured ? "Quizzes are a retention tool" : "Reward economics needs configuration"}</strong><p>{metrics.economicsConfigured ? `${money(Math.abs(quiz?.margin ?? 0))} in deliberate daily retention cost. Quizzes have no direct revenue and are not a performance problem.` : "Set the USD economic cost of one PZP before relying on profitability totals."}</p><button onClick={() => setActive("methods")}>View earn methods →</button></div></div>
          <div className="mini-health"><span><i className="good" />Read-only metrics</span><span><i className="watch" />Recorded revenue only</span></div>
        </article>
      </section>
      <LaunchMetrics />
      <MethodTable compact onViewAll={() => setActive("profitability")} />
      <section className="provider-strip">
        <div><p className="eyebrow">Data sources</p><h3>Provider health</h3></div>
        {providers.map((provider) => <div className="provider-mini" key={provider.name}><span className={`provider-dot ${provider.tone}`} /> <span><strong>{provider.name}</strong><small>{provider.status}</small></span></div>)}
      </section>
    </>
  );
}

function LaunchMetrics() {
  const { retention, cashouts } = useMetrics();
  const localBoundary = new Date(retention.relaunchAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  const cashoutBuckets = [
    { label: "Requested", data: cashouts.requested, note: "Submitted in this period", tone: "violet" },
    { label: "Pending / processing", data: cashouts.pendingProcessing, note: "Currently outstanding", tone: "amber" },
    { label: "Fulfilled", data: cashouts.fulfilled, note: "Actually delivered", tone: "green" },
    { label: "Failed / refunded", data: cashouts.failedRefunded, note: "Closed without delivery", tone: "rose" },
  ];
  return (
    <section className="launch-grid">
      <article className="panel launch-panel">
        <div className="panel-heading"><div><p className="eyebrow">Relaunch retention</p><h3>Earning-user cohorts</h3></div><span className="utc-badge">UTC cohorts</span></div>
        <p className="launch-explainer">Day 0 is each member’s first qualifying earning action on or after <strong>{localBoundary}</strong> in your local time. Migration and administrative credits are excluded.</p>
        <div className="retention-grid">
          {retention.metrics.map((metric) => <div className="retention-card" key={metric.day}><span>D{metric.day}</span><RetentionValue day={metric.day} rate={metric.rate} /><small>{metric.eligible > 0 ? `${metric.retained} of ${metric.eligible} returned` : `Available after ${metric.day === 1 ? "24 hours" : `${metric.day} days`}`}</small></div>)}
        </div>
        <p className="panel-note">{retention.cohortUsers.toLocaleString("en-US")} post-relaunch earning users. Dates are stored and evaluated in UTC; timestamps are displayed in your local timezone.</p>
        <p className="disclosure-note">Migrated users&apos; cohort day 0 is their first post-relaunch earning action. Pre-migration history is not reconstructable.</p>
      </article>
      <article className="panel launch-panel">
        <div className="panel-heading"><div><p className="eyebrow">Cash-out reporting</p><h3>Requested versus delivered</h3></div><span className="actual-badge">Fulfillment based</span></div>
        <div className="cashout-grid">
          {cashoutBuckets.map((bucket) => <div className={`cashout-row ${bucket.tone}`} key={bucket.label}><span><strong>{bucket.label}</strong><small>{bucket.note}</small></span><span><strong>{money(bucket.data.usd)}</strong><small>{bucket.data.count} requests</small></span></div>)}
        </div>
        <p className="panel-note">Delivered value uses fulfilled redemptions, with <code>updatedAt</code> as fallback when <code>fulfilledAt</code> is missing ({cashouts.fulfilledAtFallbacks} current fallback{cashouts.fulfilledAtFallbacks === 1 ? "" : "s"}). Exact Reloadly charges are reconciled manually.</p>
      </article>
    </section>
  );
}

function MetricCard({ label, value, delta, note, tone }: { label: string; value: string; delta: string; note: string; tone: string }) {
  return <article className={`metric-card ${tone}`}><div className="metric-top"><span>{label}</span><i>↗</i></div><strong>{value}</strong><div className="metric-foot"><b>{delta}</b><span>{note}</span></div></article>;
}

function MethodTable({ compact = false, onViewAll }: { compact?: boolean; onViewAll?: () => void }) {
  const methods = useMetrics().methods;
  const rows = compact ? methods.slice(0, 5) : methods;
  return (
    <section className="panel table-panel">
      <div className="panel-heading"><div><p className="eyebrow">Unit economics</p><h3>Earn method performance</h3></div>{onViewAll && <button className="text-button" onClick={onViewAll}>View analysis →</button>}</div>
      <div className="table-scroll"><table><thead><tr><th>Method</th><th>Users</th><th>Completion</th><th>Revenue</th><th>Reward cost</th><th>Margin</th><th>Status</th></tr></thead><tbody>{rows.map((method) => <tr key={method.name}><td data-label="Method"><span className={`method-icon ${method.tone}`}>{method.name.charAt(0)}</span><span><strong>{method.name}</strong><small>{method.provider}</small>{method.name === "Games" && <small className="method-disclosure" title={PLAYGAMA_LIMITATION}>Revenue understated · shared-pool attribution limitation</small>}</span></td><td data-label="Users">{method.users}</td><td data-label="Completion">{method.completion}</td><td data-label="Revenue">{method.revenueEstimated ? "≈" : ""}{money(method.revenue)}</td><td data-label="Reward cost">{method.cost === null ? "Not set" : money(method.cost)}</td><td data-label="Margin" className={(method.margin ?? 0) < 0 ? "negative" : "positive"}>{method.margin === null ? "Not set" : <>{method.revenueEstimated ? "≈" : ""}{money(method.margin)}</>}</td><td data-label="Status"><MethodStatus method={method} showNote={method.name === "Quizzes"} /></td></tr>)}</tbody></table></div>
      <p className="table-limitation"><strong>Games / Playgama:</strong> {PLAYGAMA_LIMITATION}</p>
    </section>
  );
}

function Profitability() {
  const { summary, revenueBreakdown } = useMetrics();
  return <><PageIntro eyebrow="Unit economics" title="See what earns—and what costs." copy="Revenue, economic reward cost, and contribution margin normalized across every earning method." /><MethodTable /><section className="formula-panel"><span className="formula-icon">∑</span><div><strong>Contribution margin</strong><p>Recorded provider revenue − economic cost of issued PZP</p><div className="revenue-breakdown"><span>Surveys + offers <b>{money(revenueBreakdown.earnMethodRevenue)}</b></span><span>Display / banner ads <b>{money(revenueBreakdown.displayAdRevenue)}</b></span>{revenueBreakdown.otherAttributedRevenue > .005 && <span>Other attributed revenue <b>{money(revenueBreakdown.otherAttributedRevenue)}</b></span>}</div></div><div className="formula-values"><span><small>Total recorded revenue</small><strong>{money(summary.grossRevenue)}</strong></span><span><small>Accrued margin</small><strong>{summary.contributionMargin === null ? "Not set" : money(summary.contributionMargin)}</strong></span></div></section></>;
}

function EarnMethods() {
  const methods = useMetrics().methods;
  return <><PageIntro eyebrow="Engagement" title="Every way members earn." copy="Participation, completion, revenue, and reward expense for each Perza experience." /><section className="method-card-grid">{methods.map((method) => <article className="method-card" key={method.name}><span className={`method-icon large ${method.tone}`}>{method.name.charAt(0)}</span><div className="method-title"><div><h3>{method.name}</h3><p>{method.provider}</p></div><MethodStatus method={method} /></div><div className="method-stats"><span><small>Revenue</small><strong>{method.revenueEstimated ? "≈" : ""}{money(method.revenue)}</strong></span><span><small>Reward cost</small><strong>{method.cost === null ? "Not set" : money(method.cost)}</strong></span><span><small>Margin</small><strong className={(method.margin ?? 0) < 0 ? "negative" : "positive"}>{method.margin === null ? "Not set" : <>{method.revenueEstimated ? "≈" : ""}{money(method.margin)}</>}</strong></span></div>{method.name === "Quizzes" && <p className="method-card-note neutral">{QUIZ_RETENTION_NOTE}</p>}{method.name === "Games" && <p className="method-card-note">{PLAYGAMA_LIMITATION}</p>}<div className="method-user-row"><span>{method.users} earners</span><span>{method.completion}</span></div></article>)}</section></>;
}

function Providers() {
  const providers = useMetrics().providers;
  return <><PageIntro eyebrow="Connections" title="One view across every provider." copy="Reporting freshness, connection state, and latest revenue signals—without labeling partial integrations as complete." /><section className="provider-grid">{providers.map((provider) => { const ready = provider.status === "Connected" || provider.status === "Ready"; return <article className="provider-card" key={provider.name}><div className="provider-card-top"><span className={`provider-logo ${provider.tone}`}>{provider.name.charAt(0)}</span><span className={ready ? "status healthy" : "status warning"}>{provider.status}</span></div><h3>{provider.name}</h3><p>{provider.note}</p><div className="provider-value"><strong>{provider.value}</strong><span>{provider.detail}</span></div><div className="provider-sync"><i className={ready ? "good" : "watch"} />{ready ? "Verified live integration" : provider.status === "Ledger only" ? "Events recorded; revenue feed missing" : "Reporting setup required"}</div></article>; })}</section></>;
}

function Reconciliation() {
  const metrics = useMetrics();
  const provider = (name: string) => metrics.providers.find((item) => item.name === name);
  const checks = [
    ["TapResearch callbacks", provider("TapResearch")?.detail ?? "No ledger signal", provider("TapResearch")?.status === "Connected" ? "Recorded" : "Pending"],
    ["ayeT callbacks", provider("ayeT")?.detail ?? "No ledger signal", provider("ayeT")?.status === "Connected" ? "Recorded" : "Pending"],
    ["AdMob earnings", provider("AdMob")?.note ?? "No reporting feed", provider("AdMob")?.status === "Connected" ? "Recorded" : "Pending"],
    ["Playgama revenue", provider("Playgama")?.note ?? "No reporting feed", provider("Playgama")?.status === "Connected" ? "Recorded" : "Pending"],
    ["GA4 engagement", provider("Google Analytics")?.note ?? "No reporting feed", provider("Google Analytics")?.status === "Connected" ? "Recorded" : "Pending"],
  ];
  const recorded = checks.filter((item) => item[2] === "Recorded").length;
  return <><PageIntro eyebrow="Trust the numbers" title="Reporting reconciliation." copy="Compare provider totals against the Perza ledger before making decisions." /><section className="panel reconciliation"><div className="recon-score"><span>{recorded}/{checks.length}</span><div><strong>Sources with recorded signals</strong><p>Reconciliation is only marked complete when both provider and Perza totals are available.</p></div></div>{checks.map(([name, detail, status]) => <div className="recon-row" key={name}><span className={`check-mark ${status === "Recorded" ? "matched" : "pending"}`}>{status === "Recorded" ? "✓" : "!"}</span><div><strong>{name}</strong><p>{detail}</p></div><span className={`status ${status === "Recorded" ? "healthy" : "warning"}`}>{status}</span></div>)}</section></>;
}

function Settings() {
  return <><PageIntro eyebrow="Portal controls" title="Safe by default." copy="The portal uses a read-only reporting service and the existing Perza role model." /><section className="settings-grid"><article className="panel settings-card"><h3>Access control</h3><p>Firebase proves account identity, then Perza’s existing database role decides access. No second role system is maintained.</p><div className="setting-row"><span><strong>Firebase Authentication</strong><small>Google and Apple sign-in</small></span><span className="status healthy">Ready</span></div><div className="setting-row"><span><strong>Perza database role</strong><small>Admin or super admin</small></span><span className="status healthy">Required</span></div></article><article className="panel settings-card"><h3>Data safety</h3><p>Every API request verifies the Firebase token and database role before producing aggregate data.</p><div className="setting-row"><span><strong>Business data</strong><small>Ledger, balances, and rewards</small></span><span className="status healthy">Read only</span></div><div className="setting-row"><span><strong>Analytics projection</strong><small>First post-relaunch earning timestamp</small></span><span className="status healthy">Automated</span></div></article></section></>;
}

function PageIntro({ eyebrow, title, copy }: { eyebrow: string; title: string; copy: string }) {
  return <section className="page-intro"><p className="eyebrow">{eyebrow}</p><h2>{title}</h2><p>{copy}</p></section>;
}
